import { getHttp } from 'src/api'
import { PROPOSAL_LOADING_STAGES, ProposalLoadingStage } from 'src/common/components/loading/proposalLoadingStages'
import { API } from 'src/types'

import { PATH_API } from './path'

const POLL_INTERVAL_MS = 1000
const POLL_TIMEOUT_MS = 900

export interface ProposalGenerationStatusResponse {
  proposalGenerationStatus: ProposalLoadingStage | 'DONE' | null
  statusAgeMs: number | null
}

const STAGE_ORDER = PROPOSAL_LOADING_STAGES.map((config) => config.stage)

/**
 * Reports which stage of proposal generation the backend has reached.
 *
 * The status is stored per quiz session with no attempt identity, so a submission
 * that failed part-way leaves its last stage readable by the next attempt. A status
 * is only accepted once the backend's own `statusAgeMs` shows it was written after
 * this call started — that compares two durations rather than two clocks, so client
 * clock skew can't defeat it.
 *
 * Reported stages never move backwards. The caller's own fallback is not visible
 * here, so the caller stays responsible for not regressing past what it has shown.
 */
export const pollProposalGenerationStage = (
  sessionId: string,
  onStage: (stage: ProposalLoadingStage) => void
): (() => void) => {
  const startedAt = Date.now()
  let stopped = false
  let timer: number | undefined
  let latestStageIndex = -1

  const tick = async () => {
    try {
      const response: API<ProposalGenerationStatusResponse> = await getHttp(
        PATH_API.getProposalGenerationStatus(sessionId),
        { timeout: POLL_TIMEOUT_MS }
      )
      const { proposalGenerationStatus: status, statusAgeMs } = response?.data || {}

      if (stopped) return

      const belongsToThisSubmission =
        statusAgeMs !== null && statusAgeMs !== undefined && statusAgeMs <= Date.now() - startedAt

      if (belongsToThisSubmission) {
        if (status === 'DONE') {
          stopped = true
        } else {
          const stageIndex = STAGE_ORDER.indexOf(status as ProposalLoadingStage)
          if (stageIndex > latestStageIndex) {
            latestStageIndex = stageIndex
            onStage(STAGE_ORDER[stageIndex])
          }
        }
      }
    } catch {
      // The interstitial keeps its timed fallback until a poll succeeds.
    }

    if (!stopped) {
      timer = window.setTimeout(tick, POLL_INTERVAL_MS)
    }
  }

  tick()

  return () => {
    stopped = true
    if (timer !== undefined) window.clearTimeout(timer)
  }
}
